import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, FileText, Clock, Users, Check, Info } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import PageHero from "../components/PageHero";
import SEO from "../components/SEO";
import { PROGRAM_SLUGS, SITE_URL } from "../data/siteContent";

const DOCUMENTS = [
  "10th & 12th mark sheets and passing certificates",
  "School/College leaving certificate (TC/CLC)",
  "Character certificate from the last institution attended",
  "Caste certificate (if applicable)",
  "Aadhaar card and passport-size photographs",
  "Medical fitness certificate",
  "Migration certificate (for students from other boards/universities)",
  "Domicile certificate (for state quota benefits, if applicable)",
];

const NOTES = [
  "As per Indian Nursing Council norms, the minimum age for admission is 17 years on or before 31st December of the admission year.",
  "Candidates must be medically fit; a fitness certificate is required at the time of admission.",
  "Final admission is subject to verification of original documents and university enrolment.",
];

export default function Eligibility() {
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: courses.map((c) => ({
        "@type": "Question",
        name: `What is the eligibility for ${c.title} at SVS College of Nursing?`,
        acceptedAnswer: { "@type": "Answer", text: c.eligibility },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
        { "@type": "ListItem", position: 2, name: "Admissions", item: SITE_URL + "/admissions" },
        { "@type": "ListItem", position: 3, name: "Eligibility", item: SITE_URL + "/admissions/eligibility" },
      ],
    },
  ];

  return (
    <div data-testid="eligibility-page">
      <SEO
        title="Eligibility for B.Sc Nursing, GNM & ANM in Bokaro | SVS College of Nursing"
        description="Check eligibility criteria for B.Sc Nursing (10+2 PCB, 45%), GNM (10+2 any stream, 40% English) and ANM (10+2 pass) at SVS College of Nursing, Bokaro. Documents list included."
        path="/admissions/eligibility"
        jsonLd={jsonLd}
      />

      <PageHero
        overline="Admissions 2026-27"
        title={<>Eligibility &amp; <span className="italic text-gold-gradient">criteria</span></>}
        subtitle="Clear, INC-aligned eligibility for every program — plus the documents you will need at the time of admission."
      />

      {/* Per-program eligibility */}
      <section className="bg-sand py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">By Program</span>
            <h2 className="font-serif text-4xl text-navy mt-4 mb-12">Program-wise eligibility</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.08}>
                <div className="bg-white border border-navy/10 rounded-[2px] p-8 h-full flex flex-col" data-testid={`eligibility-card-${c.short_name}`}>
                  <div className="font-serif text-4xl text-gold/40">{c.short_name}</div>
                  <h3 className="font-serif text-2xl text-navy mt-4">{c.title}</h3>
                  <div className="mt-4 space-y-2 text-sm text-navy/60">
                    <span className="flex items-center gap-2"><Clock size={16} className="text-gold" /> {c.duration}</span>
                    <span className="flex items-center gap-2"><Users size={16} className="text-gold" /> {c.seats}</span>
                  </div>
                  <div className="mt-6 bg-sand border border-gold/30 rounded-[2px] p-5">
                    <div className="text-xs tracking-[0.2em] uppercase text-gold-muted font-mono mb-2">Eligibility</div>
                    <p className="text-navy/80 text-sm leading-relaxed">{c.eligibility}</p>
                  </div>
                  <Link
                    to={`/programs/${PROGRAM_SLUGS[c.short_name] || ""}`}
                    data-testid={`eligibility-program-link-${c.short_name}`}
                    className="mt-6 inline-flex items-center gap-2 text-navy font-semibold hover:text-gold transition-colors"
                  >
                    View program details <ArrowUpRight size={16} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>

          {/* General notes */}
          <Reveal delay={0.1}>
            <div className="mt-12 bg-navy text-sand rounded-[2px] p-8 lg:p-10 relative overflow-hidden" data-testid="eligibility-notes">
              <div className="grain-overlay" />
              <div className="relative">
                <h3 className="font-serif text-2xl mb-6 flex items-center gap-3">
                  <Info size={24} className="text-gold" /> General criteria
                </h3>
                <ul className="space-y-4 text-sand/80 text-sm leading-relaxed max-w-3xl">
                  {NOTES.map((n, i) => (
                    <li key={i} className="flex gap-3">
                      <Check size={18} className="text-gold shrink-0 mt-0.5" /> {n}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Documents */}
      <section className="bg-white py-24" data-testid="eligibility-documents">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="text-xs tracking-[0.3em] uppercase text-gold-muted font-mono">Checklist</span>
              <h2 className="font-serif text-4xl text-navy mt-4 leading-tight">
                Documents you will <span className="italic text-gold-gradient">need</span>
              </h2>
              <p className="text-navy/60 mt-5 leading-relaxed">
                Keep originals and two sets of photocopies ready. Our admissions team verifies
                everything in a single visit.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <ul className="space-y-4">
              {DOCUMENTS.map((d, i) => (
                <Reveal key={d} delay={i * 0.05}>
                  <li className="flex gap-4 items-center bg-sand border border-navy/10 rounded-[2px] p-5">
                    <FileText size={20} className="text-gold shrink-0" />
                    <span className="text-navy/80">{d}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy py-20 text-center relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="relative max-w-2xl mx-auto px-5">
          <h2 className="font-serif text-3xl sm:text-4xl text-sand mb-4">Unsure if you qualify?</h2>
          <p className="text-sand/70 mb-8">
            Our counsellors will check your eligibility over a quick call and guide you through
            the next steps.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/admissions"
              data-testid="eligibility-cta-enquire"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-8 py-4 transition-transform duration-300 hover:scale-105"
            >
              Submit an enquiry <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/admissions/fees"
              data-testid="eligibility-cta-fees"
              className="inline-flex items-center gap-2 rounded-full border border-sand/40 text-sand font-semibold px-8 py-4 transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Fees &amp; financial guidance
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
